import { useState } from "react";
import { Plus, Shield, Smartphone } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const tabs = [
  { id: "company", label: "Company" },
  { id: "team", label: "Team" },
  { id: "notifications", label: "Notifications" },
  { id: "security", label: "Security" },
] as const;

export default function Settings() {
  const [tab, setTab] = useState<typeof tabs[number]["id"]>("company");

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Workspace preferences, team members and security." />

      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={cn("border-b-2 px-3 py-2 text-sm font-medium -mb-px",
              tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "company" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h3 className="text-sm font-semibold">Company profile</h3>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Company name</label>
              <input className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" defaultValue="Acme Retail Co." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Timezone</label>
              <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option>America/Los_Angeles (UTC-08:00)</option>
                <option>Europe/London (UTC+00:00)</option>
                <option>Asia/Kolkata (UTC+05:30)</option>
              </select>
            </div>
            <Button size="sm">Save changes</Button>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="text-sm font-semibold">Logo</h3>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-info text-white text-xl font-bold">A</div>
              <div>
                <Button variant="outline" size="sm">Upload new</Button>
                <p className="mt-1 text-xs text-muted-foreground">PNG or SVG - max 1MB</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "team" && (
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h3 className="text-sm font-semibold">Team members</h3>
            <Button size="sm"><Plus className="h-4 w-4" />Invite member</Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-5 py-3 text-left font-medium">Member</th><th className="px-5 py-3 text-left font-medium">Email</th><th className="px-5 py-3 text-left font-medium">Role</th><th className="px-5 py-3 text-left font-medium">Last active</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { n: "Jordan Mills", e: "jordan@acme.com", r: "Admin" },
                { n: "Priya Patel", e: "priya@acme.com", r: "Operator" },
                { n: "Marcus Lee", e: "marcus@acme.com", r: "Viewer" },
              ].map((m) => (
                <tr key={m.e} className="hover:bg-muted/40">
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">{m.n.split(" ").map(x=>x[0]).join("")}</div>{m.n}</div></td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{m.e}</td>
                  <td className="px-5 py-3"><span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium border",
                    m.r === "Admin" ? "border-primary/30 bg-primary/10 text-primary" :
                    m.r === "Operator" ? "border-info/30 bg-info/10 text-info" :
                    "border-border bg-muted text-muted-foreground")}>{m.r}</span></td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">2 hours ago</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "notifications" && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-5 py-3 text-left font-medium">Event</th><th className="px-5 py-3 text-center font-medium">Email</th><th className="px-5 py-3 text-center font-medium">In-app</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {["Template approved","Template rejected","Channel quality drop","Balance low","Broadcast completed","Webhook delivery failure"].map((evt) => (
                <tr key={evt}>
                  <td className="px-5 py-3">{evt}</td>
                  <td className="px-5 py-3 text-center"><input type="checkbox" defaultChecked className="rounded" /></td>
                  <td className="px-5 py-3 text-center"><input type="checkbox" defaultChecked className="rounded" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "security" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">Two-factor authentication</h3></div>
            <p className="mt-1 text-xs text-muted-foreground">Add an extra layer of security with a TOTP app.</p>
            <Button size="sm" className="mt-4">Enable 2FA</Button>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">Active sessions</h3></div>
            <ul className="mt-3 space-y-2 text-xs">
              <li className="flex justify-between"><span>MacBook Pro - Chrome - San Francisco</span><span className="text-success">Current</span></li>
              <li className="flex justify-between"><span>iPhone 15 - Safari - San Francisco</span><span className="text-muted-foreground">2h ago</span></li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
